import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Minus, Plus, Trash2, Loader2, ShoppingBag, ArrowLeft } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { API, formatINR } from "@/constants";

const INPUT_CLS =
  "h-12 w-full border border-forest/20 bg-transparent px-4 text-sm text-forest placeholder:text-sage/60 focus:outline-none focus:ring-1 focus:ring-forest transition-colors duration-300";

const SLOTS = ["9 AM – 12 PM", "12 – 3 PM", "3 – 6 PM", "6 – 9 PM"];

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function CartDrawer() {
  const { items, updateQty, removeItem, total, isOpen, setIsOpen } = useCart();
  const [step, setStep] = useState("cart");
  const [customer, setCustomer] = useState({ name: "", phone: "", email: "", address: "" });
  const [deliveryDate, setDeliveryDate] = useState("");
  const [deliverySlot, setDeliverySlot] = useState("");
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const paypalContainerRef = useRef(null);
  const [paypalRendered, setPaypalRendered] = useState(false);

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    axios.get(`${API}/payments/config`).then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  const buildPayload = () => ({
    items: items.map((i) => ({ slug: i.slug, qty: i.qty })),
    customer: {
      ...customer,
      email: customer.email || null,
      address: customer.address || null,
      delivery_date: deliveryDate || null,
      delivery_slot: deliverySlot || null,
    },
    origin_url: window.location.origin,
  });

  useEffect(() => {
    if (step !== "details" || !config?.paypal_enabled || paypalRendered) return;
    let cancelled = false;
    const renderButtons = async () => {
      if (!window.paypal) {
        await new Promise((resolve) => {
          const s = document.createElement("script");
          s.src = `https://www.paypal.com/sdk/js?client-id=${config.paypal_client_id}&currency=${config.paypal_currency || "USD"}`;
          s.onload = resolve;
          s.onerror = resolve;
          document.body.appendChild(s);
        });
      }
      if (cancelled || !window.paypal || !paypalContainerRef.current) return;
      try {
        await window.paypal
          .Buttons({
            style: { layout: "vertical", color: "black", shape: "rect", label: "paypal" },
            createOrder: async () => {
              if (!customer.name.trim() || !customer.phone.trim()) {
                toast.error("Please fill your name and phone first.");
                throw new Error("missing-customer");
              }
              const { data } = await axios.post(`${API}/orders/paypal/create`, buildPayload());
              return data.paypal_order_id;
            },
            onApprove: async (d) => {
              try {
                const { data } = await axios.post(`${API}/orders/paypal/capture`, {
                  paypal_order_id: d.orderID,
                });
                window.location.href = `/payment/success?order_id=${data.order_id}`;
              } catch {
                toast.error("Payment could not be confirmed — please call us.");
              }
            },
            onError: () => {},
          })
          .render(paypalContainerRef.current);
        if (!cancelled) setPaypalRendered(true);
      } catch {
        // PayPal buttons failed to render; other payment methods remain available
      }
    };
    renderButtons();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, config, paypalRendered]);

  const set = (k) => (e) => setCustomer((c) => ({ ...c, [k]: e.target.value }));

  const close = (open) => {
    setIsOpen(open);
    if (!open)
      setTimeout(() => {
        setStep("cart");
        setPaypalRendered(false);
      }, 300);
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = buildPayload();
    try {
      if (config?.razorpay_enabled) {
        const loaded = await loadRazorpayScript();
        if (!loaded) throw new Error("razorpay-load");
        const { data } = await axios.post(`${API}/orders/razorpay/checkout`, payload);
        const rzp = new window.Razorpay({
          key: data.key_id,
          amount: data.amount,
          currency: data.currency,
          name: "Utsav Flowers",
          description: "Fresh flowers & gifts",
          order_id: data.razorpay_order_id,
          prefill: { name: customer.name, contact: customer.phone, email: customer.email || "" },
          theme: { color: "#0F2119" },
          handler: async (resp) => {
            try {
              await axios.post(`${API}/orders/razorpay/verify`, {
                order_id: data.order_id,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              window.location.href = `/payment/success?order_id=${data.order_id}`;
            } catch {
              toast.error("Payment verification failed — please call us and we will confirm.");
              setLoading(false);
            }
          },
          modal: { ondismiss: () => setLoading(false) },
        });
        rzp.open();
      } else {
        const { data } = await axios.post(`${API}/orders/checkout`, payload);
        window.location.href = data.checkout_url;
      }
    } catch {
      toast.error("Could not start checkout. Please try again or order on WhatsApp.");
      setLoading(false);
    }
  };

  const handleUpiOrder = async () => {
    if (!customer.name.trim() || !customer.phone.trim()) {
      toast.error("Please fill your name and phone first.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/orders/upi`, buildPayload());
      window.location.href = `/payment/success?order_id=${data.order_id}&method=upi`;
    } catch {
      toast.error("Could not place the order. Please try again or order on WhatsApp.");
      setLoading(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={close}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="flex w-full flex-col border-l border-forest/15 bg-cream p-0 sm:max-w-md"
      >
        <SheetHeader className="border-b border-forest/10 px-8 py-6">
          <SheetTitle className="flex items-center gap-3 font-serif text-2xl italic text-forest">
            {step === "details" && (
              <button onClick={() => setStep("cart")} data-testid="checkout-back-button" aria-label="Back to cart">
                <ArrowLeft size={20} />
              </button>
            )}
            {step === "cart" ? "Your Basket" : "Delivery Details"}
          </SheetTitle>
        </SheetHeader>

        {step === "cart" ? (
          <>
            <div className="flex-1 overflow-y-auto px-8 py-6">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-4 text-center" data-testid="cart-empty-state">
                  <ShoppingBag size={40} strokeWidth={1} className="text-sage/50" />
                  <p className="font-serif text-xl italic text-sage">Your basket is empty —<br />the flowers are waiting.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {items.map((item) => (
                    <div key={item.slug} className="flex gap-4 border-b border-forest/10 pb-6" data-testid={`cart-item-${item.slug}`}>
                      <img src={item.image} alt={item.name} className="h-20 w-16 border border-forest/15 object-cover" />
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-serif text-base font-medium leading-snug text-forest">{item.name}</p>
                          <button onClick={() => removeItem(item.slug)} data-testid={`cart-remove-${item.slug}`} className="text-sage transition-colors hover:text-terracotta" aria-label="Remove item">
                            <Trash2 size={15} />
                          </button>
                        </div>
                        <div className="mt-auto flex items-center justify-between pt-3">
                          <div className="flex items-center border border-forest/20">
                            <button onClick={() => updateQty(item.slug, item.qty - 1)} data-testid={`cart-qty-minus-${item.slug}`} className="flex h-8 w-8 items-center justify-center transition-colors hover:bg-forest hover:text-cream" aria-label="Decrease quantity">
                              <Minus size={13} />
                            </button>
                            <span className="w-8 text-center text-sm font-semibold" data-testid={`cart-qty-${item.slug}`}>{item.qty}</span>
                            <button onClick={() => updateQty(item.slug, item.qty + 1)} data-testid={`cart-qty-plus-${item.slug}`} className="flex h-8 w-8 items-center justify-center transition-colors hover:bg-forest hover:text-cream" aria-label="Increase quantity">
                              <Plus size={13} />
                            </button>
                          </div>
                          <span className="font-serif text-lg italic text-terracotta">{formatINR(item.price * item.qty)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {items.length > 0 && (
              <div className="border-t border-forest/10 px-8 py-6">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-sage">Subtotal</span>
                  <span className="font-serif text-2xl italic text-forest" data-testid="cart-subtotal">{formatINR(total)}</span>
                </div>
                <p className="mb-5 text-xs text-sage">Fresh flowers are hand-delivered across Dibrugarh.</p>
                <button
                  onClick={() => setStep("details")}
                  data-testid="cart-checkout-button"
                  className="w-full bg-forest py-4 text-xs font-bold uppercase tracking-[0.25em] text-cream transition-colors duration-300 hover:bg-marigold hover:text-forest"
                >
                  Proceed to Checkout
                </button>
              </div>
            )}
          </>
        ) : (
          <form onSubmit={handleCheckout} className="flex flex-1 flex-col overflow-y-auto px-8 py-6" data-testid="checkout-form">
            <div className="space-y-5">
              <div>
                <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.2em] text-sage" htmlFor="checkout-name">Name *</label>
                <input id="checkout-name" data-testid="checkout-name-input" required value={customer.name} onChange={set("name")} placeholder="Full name" className={INPUT_CLS} />
              </div>
              <div>
                <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.2em] text-sage" htmlFor="checkout-phone">Phone *</label>
                <input id="checkout-phone" data-testid="checkout-phone-input" required value={customer.phone} onChange={set("phone")} placeholder="+91 ..." className={INPUT_CLS} />
              </div>
              <div>
                <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.2em] text-sage" htmlFor="checkout-email">Email</label>
                <input id="checkout-email" data-testid="checkout-email-input" type="email" value={customer.email} onChange={set("email")} placeholder="you@example.com" className={INPUT_CLS} />
              </div>
              <div>
                <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.2em] text-sage" htmlFor="checkout-address">Delivery Address</label>
                <textarea id="checkout-address" data-testid="checkout-address-input" rows={3} value={customer.address} onChange={set("address")} placeholder="House, street, landmark — Dibrugarh" className="w-full border border-forest/20 bg-transparent px-4 py-3 text-sm text-forest placeholder:text-sage/60 focus:outline-none focus:ring-1 focus:ring-forest transition-colors duration-300" />
              </div>
              <div>
                <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.2em] text-sage" htmlFor="checkout-delivery-date">Delivery Date</label>
                <input
                  id="checkout-delivery-date"
                  data-testid="checkout-delivery-date-input"
                  type="date"
                  min={today}
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  className={INPUT_CLS}
                />
              </div>
              <div>
                <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.2em] text-sage">Delivery Time Slot</label>
                <div className="grid grid-cols-2 gap-2" data-testid="checkout-delivery-slots">
                  {SLOTS.map((slot) => (
                    <button
                      key={slot}
                      type="button"
                      data-testid={`delivery-slot-${slot.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                      onClick={() => setDeliverySlot(deliverySlot === slot ? "" : slot)}
                      className={`border px-2 py-3 text-[11px] font-bold uppercase tracking-wider transition-colors duration-300 ${
                        deliverySlot === slot
                          ? "border-forest bg-forest text-cream"
                          : "border-forest/20 text-forest/60 hover:border-forest hover:text-forest"
                      }`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-auto pt-8">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-sage">Total</span>
                <span className="font-serif text-2xl italic text-forest" data-testid="checkout-total">{formatINR(total)}</span>
              </div>
              {config?.razorpay_enabled ? (
                <>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="checkout-pay-button"
                    className="flex w-full items-center justify-center gap-3 bg-forest py-4 text-xs font-bold uppercase tracking-[0.25em] text-cream transition-colors duration-300 hover:bg-marigold hover:text-forest disabled:opacity-60"
                  >
                    {loading && <Loader2 size={16} className="animate-spin" />}
                    {loading ? "Opening secure payment..." : `Pay ${formatINR(total)} — UPI / Cards`}
                  </button>
                  <div className="my-5 flex items-center gap-3">
                    <span className="h-px flex-1 bg-forest/15" />
                    <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-sage">or</span>
                    <span className="h-px flex-1 bg-forest/15" />
                  </div>
                  <button
                    type="button"
                    onClick={handleUpiOrder}
                    disabled={loading}
                    data-testid="checkout-upi-button"
                    className="flex w-full items-center justify-center gap-3 border border-forest py-4 text-xs font-bold uppercase tracking-[0.25em] text-forest transition-colors duration-300 hover:bg-forest hover:text-cream disabled:opacity-60"
                  >
                    Pay Direct via UPI — Paytm / GPay / PhonePe
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleUpiOrder}
                    disabled={loading}
                    data-testid="checkout-upi-button"
                    className="flex w-full items-center justify-center gap-3 bg-forest py-4 text-xs font-bold uppercase tracking-[0.25em] text-cream transition-colors duration-300 hover:bg-marigold hover:text-forest disabled:opacity-60"
                  >
                    {loading && <Loader2 size={16} className="animate-spin" />}
                    Pay {formatINR(total)} via UPI — Paytm / GPay
                  </button>
                  <p className="mt-2 text-center text-[11px] text-sage">
                    Real payment — place the order now, pay to our UPI ID on the next screen.
                  </p>
                  <div className="my-5 flex items-center gap-3">
                    <span className="h-px flex-1 bg-forest/15" />
                    <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-sage">or</span>
                    <span className="h-px flex-1 bg-forest/15" />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="checkout-pay-button"
                    className="flex w-full items-center justify-center gap-3 border border-forest/30 py-4 text-xs font-bold uppercase tracking-[0.25em] text-forest/70 transition-colors duration-300 hover:border-forest hover:text-forest disabled:opacity-60"
                  >
                    {loading && <Loader2 size={16} className="animate-spin" />}
                    {loading ? "Opening secure payment..." : "Pay by Card (demo mode)"}
                  </button>
                  <p className="mt-2 text-center text-[11px] text-sage">
                    Card payments are in demo mode — real cards will not work here yet.
                  </p>
                </>
              )}
              {config?.paypal_enabled && (
                <div className="mt-5" data-testid="paypal-section">
                  <div className="mb-4 flex items-center gap-3">
                    <span className="h-px flex-1 bg-forest/15" />
                    <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-sage">or pay with</span>
                    <span className="h-px flex-1 bg-forest/15" />
                  </div>
                  <div ref={paypalContainerRef} data-testid="paypal-button-container" />
                  <p className="mt-3 text-center text-[11px] text-sage">
                    PayPal charges in {config.paypal_currency || "USD"} — your ₹ total is converted automatically.
                  </p>
                </div>
              )}
            </div>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
}
